/**
 * ECS Client Public API
 * -----------------------------------------------------------------------------
 * This file re-exports the main ECS client and configuration functions for use
 * throughout the application. Provides a clean, stable API while maintaining
 * backwards compatibility with the existing ecsClient.ts implementation.
 */

// Feature Flag Registry exports (for advanced usage)
export {
  FEATURE_FLAG_REGISTRY,
  FEATURE_FLAGS,
  getFeatureFlagDefault,
  getFeatureFlagDescription,
  getAllFeatureFlags,
} from './featureFlagRegistry';

export type {
  FeatureFlag,
  FeatureFlagTypes,
} from './featureFlagRegistry';

// Configuration exports
export {
  transformEcsDataToAppConfig,
  validateConfiguration,
  mergeWithDefaults,
  createConfigDiff,
  areConfigurationsEqual,
  getConfigurationSummary,
} from './appConfiguration';

export {
  FEATURE_FLAG_DEFAULTS,
  isValidFlagValue,
  getDefaultConfig,
} from './featureFlagsDefaults';

// Type definitions
export type {
  AppConfiguration,
  EcsInitOptions,
  EcsConfigState,
  ConfigChangeListener,
  EcsFetchResult,
  EcsClientOptions,
  UserFilterInfo,
} from './types';

export {
  EcsClientStatus,
} from './types';

// State management (for advanced usage)
export {
  getEcsClient,
  getCurrentConfig,
  getStateSnapshot,
} from './state';

// Logging utilities
export {
  logInfo,
  logWarn,
  logError,
  logDebug,
  setLogLevel,
  getLogLevel,
  LogLevel,
} from './logger';

// Storage utilities
export {
  LocalStorageWrapper,
} from './storage';

// Endpoints utilities
export {
  createEndpointsManager,
  GlobalContext,
  consumeTelemetryData,
} from './endpoints';

// Real implementations using our modular architecture
import { createEcsClientWithCache, EcsDomain, EcsRequestStatus } from "@1js/ecs-client";
import type {
  IEcsParameters,
  IEcsFilters, 
  IEcsData,
  FetchPrecheckFunction,
  FetchPrecheckResult 
} from "@1js/ecs-client";
import type { AccountInfo } from "@azure/msal-browser";
import type { AppConfiguration, EcsInitOptions, ConfigChangeListener, EcsFetchResult } from './types';
import { ConsoleLoggerFactory, logInfo, logWarn, logError } from './logger';
import { LocalStorageWrapper } from './storage';
import { createEndpointsManager } from './endpoints';
import {
  getEcsClient,
  setEcsClient, 
  getCurrentConfig,
  setCurrentConfig,
  addConfigChangeListener,
  notifyConfigChangeListeners,
  resetState,
} from './state';
import { transformEcsDataToAppConfig } from './appConfiguration';
import { getDefaultConfig } from './featureFlagsDefaults';

// Constants (these should come from environment config)
const ECS_CLIENT = "Skype";
const ECS_VERSION = "1.0.0.0";
const ECS_AGENTS = ["FrontlineBYOD"];
const ECS_SERVICE_NAME = "FrontlineBYOD";

/**
 * Get ECS parameters for client initialization (no user filtering - anonymous access).
 */
function getEcsParameters(): IEcsParameters {
  logInfo('Using ECS parameters without user filters', 'ECS_PARAMS');
  
  return {
    client: ECS_CLIENT,
    version: ECS_VERSION,
    domain: EcsDomain.Prod_ConsumerClientApps,
    agents: ECS_AGENTS,
    filters: {},
  };
}

/**
 * Create a fetch precheck function to control when fetches should occur
 */
function createFetchPrecheck(): FetchPrecheckFunction {
  return (): FetchPrecheckResult => {
    if (!navigator.onLine) {
      return {
        errorMessage: "No network connection available",
        requestStatus: EcsRequestStatus.Abandoned_NoNetwork,
      };
    }
    return true;
  };
}

/**
 * Load configuration from ECS Client
 */
async function loadConfiguration(): Promise<void> {
  const client = getEcsClient();
  if (!client) {
    setCurrentConfig({ featureFlags: getDefaultConfig() });
    return;
  }

  try {
    // Fetch from ECS server (will also cache the result)
    await client.fetch();
    
    // Get the fetched/cached data
    const ecsData = await client.get();
    
    if (ecsData) {
      const config = transformEcsDataToAppConfig(ecsData);
      setCurrentConfig(config);
      logInfo('ECS configuration loaded successfully', 'CONFIG');
    } else {
      logInfo('No ECS data available, using defaults', 'CONFIG');
      setCurrentConfig({ featureFlags: getDefaultConfig() });
    }
  } catch (error) {
    logError('Failed to load ECS configuration', error, 'CONFIG');
    setCurrentConfig({ featureFlags: getDefaultConfig() });
  }
}

/**
 * Initialize the ECS client without user context.
 * ECS configuration is fetched once and cached for the app lifetime.
 */
export async function initializeEcsClient(): Promise<void> {
  const currentClient = getEcsClient();
  if (currentClient) {
    logInfo('ECS Client already initialized', 'INIT');
    return;
  }

  try {
    logInfo('Initializing ECS Client...', 'INIT');
    
    // Get ECS parameters without user filters
    const parameters = getEcsParameters();
    
    const client = await createEcsClientWithCache({
      serviceName: ECS_SERVICE_NAME,
      loggerFactory: new ConsoleLoggerFactory(),
      ecsParameters: parameters,
      endpointsManager: createEndpointsManager(),
      cacheOptions: {
        storage: new LocalStorageWrapper(),
      },
      fetchRetryPolicy: {
        retryType: "exponential-backoff",
        baseDelayMs: 3000,
        exponentialFactor: 2.0,
        maxRetries: 2,
      },
      fetchPrecheck: createFetchPrecheck(),
    });

    setEcsClient(client);
    logInfo('ECS Client initialized successfully', 'INIT');
    
    // Load initial configuration
    await loadConfiguration();
    
    logInfo('ECS Client initialization complete with configuration loaded', 'INIT');
    
  } catch (error) {
    logWarn('ECS Client initialization failed, using defaults', 'INIT', error);
    setCurrentConfig({ featureFlags: getDefaultConfig() });
  }
}

/**
 * Get the current application configuration.
 * @returns The current configuration object
 */
export function getAppConfiguration(): AppConfiguration {
  const config = getCurrentConfig();
  return config || { featureFlags: getDefaultConfig() };
}

/**
 * Get a specific feature flag value with type safety.
 * @param flagName - The name of the feature flag
 * @param defaultValue - Optional fallback value
 * @returns The feature flag value
 */
export function getFeatureFlag<K extends keyof AppConfiguration['featureFlags']>(
  flagName: K,
  defaultValue?: AppConfiguration['featureFlags'][K]
): AppConfiguration['featureFlags'][K] {
  const config = getAppConfiguration();
  return config.featureFlags[flagName] ?? defaultValue ?? (false as AppConfiguration['featureFlags'][K]);
}

/**
 * Refresh the configuration from ECS.
 * @returns Promise that resolves when refresh is complete
 */
export async function refreshConfiguration(): Promise<void> {
  await loadConfiguration();
}

/**
 * Subscribe to configuration changes.
 * @param listener - Function to call when configuration changes
 * @returns Unsubscribe function
 */
export function subscribeToConfigChanges(listener: ConfigChangeListener): () => void {
  return addConfigChangeListener(listener);
}

/**
 * Force fetch fresh configuration from ECS, bypassing cache.
 * @returns Promise resolving to fetch result
 */
export async function forceFreshFetch(): Promise<EcsFetchResult> {
  const client = getEcsClient();
  if (!client) {
    logInfo('ECS Client not initialized for fresh fetch', 'FORCE_FETCH');
    return {
      success: false,
      error: 'ECS Client not initialized',
      timestamp: Date.now(),
    };
  }

  try {
    // Clear cache for fresh data
    try {
      const storage = new LocalStorageWrapper();
      await storage.removeItem('ecs.cache.FrontlineBYOD');
      logInfo('ECS cache cleared for fresh fetch', 'FORCE_FETCH');
    } catch (error) {
      logWarn('Failed to clear ECS cache', 'FORCE_FETCH', error);
    }

    const data = await client.fetch();
    logInfo(`Fresh fetch completed with status: ${EcsRequestStatus[data.requestStatus]}`, 'FORCE_FETCH');
    
    if (data.requestStatus === EcsRequestStatus.Success && data.ecsConfiguration) {
      const config = transformEcsDataToAppConfig(data);
      setCurrentConfig(config);
      
      // Notify listeners
      notifyConfigChangeListeners(config);
      
      return {
        success: true,
        config,
        timestamp: Date.now(),
      };
    } else if (data.requestStatus === EcsRequestStatus.Success_NoChanges) {
      logInfo('No changes detected, current config up to date', 'FORCE_FETCH');
      return {
        success: true,
        config: getCurrentConfig() || { featureFlags: getDefaultConfig() },
        fromCache: true,
        timestamp: Date.now(),
      };
    }
    
    return {
      success: false,
      error: `ECS fetch failed with status: ${EcsRequestStatus[data.requestStatus]}`,
      timestamp: Date.now(),
    };
  } catch (error) {
    logError('Fresh fetch failed', error, 'FORCE_FETCH');
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
      timestamp: Date.now(),
    };
  }
}

/**
 * Force fetch configuration (legacy compatibility).
 * @returns Promise resolving to ECS data or null
 */
export async function forceFetch(): Promise<IEcsData | null> {
  const client = getEcsClient();
  if (!client) {
    logInfo('ECS Client not initialized for force fetch', 'FORCE_FETCH_LEGACY');
    return null;
  }

  try {
    const data = await client.fetch();
    if (data.requestStatus === EcsRequestStatus.Success && data.ecsConfiguration) {
      const config = transformEcsDataToAppConfig(data);
      setCurrentConfig(config);
      notifyConfigChangeListeners(config);
    }
    return data;
  } catch (error) {
    logError('Force fetch failed', error, 'FORCE_FETCH_LEGACY');
    return null;
  }
}

/**
 * Quick kill switch check for pre-authentication scenarios.
 * @returns Promise resolving to kill switch status
 */
export async function checkKillSwitchQuick(): Promise<boolean> {
  try {
    // First check current cached config
    const config = getAppConfiguration();
    const currentKillSwitch = config.featureFlags.killSwitch;
    
    // If we don't have an ECS client yet, try to initialize it minimally
    const client = getEcsClient();
    if (!client) {
      try {
        await initializeEcsClient();
      } catch (error) {
        logWarn('Failed to initialize ECS client for kill switch check, using cached value', 'KILL_SWITCH', error);
        return currentKillSwitch;
      }
    }
    
    // Try to fetch fresh configuration
    try {
      const result = await forceFreshFetch();
      if (result.success && result.config) {
        return result.config.featureFlags.killSwitch;
      } else {
        logWarn('Failed to fetch fresh kill switch status, using current config', 'KILL_SWITCH');
        return currentKillSwitch;
      }
    } catch (error) {
      logWarn('Failed to fetch fresh kill switch status, using current config', 'KILL_SWITCH', error);
      return currentKillSwitch;
    }
  } catch (error) {
    logError('Kill switch check failed completely', error, 'KILL_SWITCH');
    // In case of complete failure, be conservative and check cached config
    const config = getAppConfiguration();
    return config.featureFlags.killSwitch;
  }
}

/**
 * Dispose of the ECS client and clean up resources.
 */
export function dispose(): void {
  const client = getEcsClient();
  if (client) {
    client.dispose();
  }
  
  resetState();
  logInfo('ECS Client disposed and state reset', 'DISPOSE');
}

// Legacy compatibility exports (will be deprecated)
export {
  initializeEcsClient as initializeEcsClientImpl,
  getAppConfiguration as getAppConfigurationImpl,
  getFeatureFlag as getFeatureFlagImpl,
  refreshConfiguration as refreshConfigurationImpl,
  subscribeToConfigChanges as subscribeToConfigChangesImpl,
  forceFetch as forceFetchImpl,
  dispose as disposeImpl,
};